/**
 * Get WordPress timezone-aware current time using localized timezone data
 * Falls back to browser time if WordPress time is not available
 */
function getWordPressTime() {
	try {
		// Check if we have localized WordPress timezone data
		if (typeof window.zpAlertbarDateTime !== 'undefined' && window.zpAlertbarDateTime.timezone) {
			const now = new Date();
			const timezoneString = window.zpAlertbarDateTime.timezone;
			
			// Try to use the timezone string if it looks like a proper timezone (not UTC+X format)
			if (timezoneString && !timezoneString.startsWith('UTC')) {
				try {
					// Create a date formatter for the WordPress timezone
					const formatter = new Intl.DateTimeFormat('en-CA', {
						timeZone: timezoneString,
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit',
						hour12: false
					});
					
					// Get current time in WordPress timezone
					const parts = formatter.formatToParts(now);
					const year = parseInt(parts.find(part => part.type === 'year').value);
					const month = parseInt(parts.find(part => part.type === 'month').value) - 1; // Month is 0-indexed
					const day = parseInt(parts.find(part => part.type === 'day').value);
					const hour = parseInt(parts.find(part => part.type === 'hour').value);
					const minute = parseInt(parts.find(part => part.type === 'minute').value);
					const second = parseInt(parts.find(part => part.type === 'second').value);
					
					const wpTime = new Date(year, month, day, hour, minute, second);
					
					console.log('WordPress Time (frontend, timezone-accurate):', wpTime.toLocaleString());
					console.log('Browser Time:', now.toLocaleString());
					console.log('WordPress Timezone:', timezoneString);
					console.log('Time difference (minutes):', Math.round((wpTime.getTime() - now.getTime()) / (1000 * 60)));
					
					return wpTime;
				} catch (error) {
					console.warn('Error using timezone string on frontend, falling back to offset:', error);
				}
			}
			
			// Fallback to offset calculation for UTC+X format or if timezone string fails
			const gmtOffset = parseFloat(window.zpAlertbarDateTime.gmt_offset) || 0;
			const wpTime = new Date(now.getTime() + (gmtOffset * 60 * 60 * 1000));
			
			console.log('WordPress Time (frontend, offset-based):', wpTime.toLocaleString());
			console.log('Browser Time:', now.toLocaleString());
			console.log('WordPress GMT offset (hours):', gmtOffset);
			console.log('Timezone info:', window.zpAlertbarDateTime);
			
			return wpTime;
		}
		
		// Final fallback to browser time
		console.warn('No WordPress timezone data available on frontend, falling back to browser time');
		return new Date();
		
	} catch (error) {
		// Fallback to browser time with console warning
		const browserTime = new Date();
		console.warn('Error getting WordPress time on frontend, falling back to browser time:', error);
		console.log('Browser Time (fallback):', browserTime.toLocaleString());
		return browserTime;
	}
}

// Cookie utility functions
const CookieManager = {
	// Set a cookie with name, value, and expiration in days
	setCookie(name, value, days) {
		const date = new Date();
		date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
		const expires = "expires=" + date.toUTCString();
		document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
		console.log(`Cookie set: ${name}=${value} (expires in ${days} days)`);
	},

	// Get a cookie value by name
	getCookie(name) {
		const nameEQ = name + "=";
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') c = c.substring(1, c.length);
			if (c.indexOf(nameEQ) === 0) {
				return c.substring(nameEQ.length, c.length);
			}
		}
		return null;
	},

	// Delete a cookie by name
	deleteCookie(name) {
		document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
		console.log(`Cookie deleted: ${name}`);
	},

	// Generate cookie name for alert bar
	getAlertCookieName(alertId) {
		return `zp_alertbar_${alertId}_dismissed`;
	}
};

// Expiration utility functions
const ExpirationManager = {
	// Check if alert has expired based on scheduled date/time
	checkExpiration(alertBar) {
		const expirationEnabled = alertBar.getAttribute("data-expiration-enabled") === "true";
		const expirationDate = alertBar.getAttribute("data-expiration-date");
		const expirationTime = alertBar.getAttribute("data-expiration-time");
		
		if (!expirationEnabled || !expirationDate || !expirationTime) {
			return false; // No expiration set
		}
		
		try {
			// Create expiration datetime
			const expirationDateTime = new Date(`${expirationDate}T${expirationTime}`);
			const now = getWordPressTime(); // Use WordPress time instead of browser time
			const alertId = alertBar.getAttribute("data-alert-id");
			
			if (now > expirationDateTime) {
				console.log(`Alert bar expired: ${alertId} - Expiration: ${expirationDateTime.toLocaleString()}, Current: ${now.toLocaleString()}`);
				return true;
			}
			
			console.log(`Alert bar active: ${alertId} - Expires: ${expirationDateTime.toLocaleString()}, Current: ${now.toLocaleString()}`);
		} catch (error) {
			const alertId = alertBar.getAttribute("data-alert-id");
			console.warn(`Invalid expiration date/time for alert: ${alertId}`, error);
		}
		
		return false;
	},
	
	// Get formatted expiration info for debugging
	getExpirationInfo(alertBar) {
		const expirationEnabled = alertBar.getAttribute("data-expiration-enabled") === "true";
		const expirationDate = alertBar.getAttribute("data-expiration-date");
		const expirationTime = alertBar.getAttribute("data-expiration-time");
		
		return {
			enabled: expirationEnabled,
			date: expirationDate,
			time: expirationTime,
			datetime: expirationEnabled && expirationDate && expirationTime ? 
				`${expirationDate}T${expirationTime}` : null
		};
	}
};

document.addEventListener("DOMContentLoaded", () => {
	// Get all alert bars on the page
	const alertBars = document.querySelectorAll(".wp-block-zp-alertbar");

	alertBars.forEach(alertBar => {
		const alertId = alertBar.getAttribute("data-alert-id");
		const enableCookies = alertBar.getAttribute("data-enable-cookies") !== "false";
		const cookieDuration = parseInt(alertBar.getAttribute("data-cookie-duration")) || 30;
		
		// Check if alert bar should be active
		if (alertBar && alertBar.getAttribute("data-alertbar-active") === "true") {
			
			// First check if alert has expired (automatic frontend expiration)
			if (ExpirationManager.checkExpiration(alertBar)) {
				alertBar.style.display = "none";
				console.log(`Alert bar hidden due to expiration: ${alertId}`);
				return; // Skip this alert bar
			}
			
			// Generate cookie name for this alert (only if needed)
			const cookieName = CookieManager.getAlertCookieName(alertId);
			
			// Check if alert was previously dismissed (if cookies enabled)
			if (enableCookies && CookieManager.getCookie(cookieName)) {
				alertBar.style.display = "none";
				console.log(`Alert bar hidden due to cookie: ${cookieName}`);
				return; // Skip this alert bar
			}
			
			alertBar.style.display = "block"; // Show the alert bar
			
			// Add dismiss functionality
			const closeButton = alertBar.querySelector(".zp-alertbar-close");
			if (closeButton) {
				closeButton.addEventListener("click", function(event) {
					event.preventDefault();
					
					// Set cookie if cookies are enabled
					if (enableCookies) {
						CookieManager.setCookie(cookieName, "true", cookieDuration);
					}
					
					// Hide the alert bar with smooth transition
					alertBar.style.transition = "opacity 0.3s ease-out";
					alertBar.style.opacity = "0";
					
					// Remove from DOM after transition
					setTimeout(() => {
						alertBar.style.display = "none";
						alertBar.style.opacity = ""; // Reset for potential future use
					}, 300);
					
					// Debug logging with alert ID and cookie info
					console.log(`Alert bar dismissed - ID: ${alertId}, Cookie: ${enableCookies ? cookieName : 'disabled'}`, alertBar);
				});
			}
		} else {
			alertBar.style.display = "none"; // Hide the alert bar if not active
		}
	});
});
